import { RootState } from '@/types/graph';
import { isPatientInformationComplete } from '@/util/patient';
import { isKioskOnboarding } from '@/util/kiosk';
import { isWebsiteDemoPractice } from '@/util/demoPractice';
import { resolvePatientDataCollectionConfig } from '@aldebaran/common/util/patientDataCollection';
import { OnboardingContext } from './types';

/**
 * Builds OnboardingContext from Redux state.
 * This is the ONLY place where Redux state is translated into flow variables.
 *
 * @param state - Redux root state
 * @returns OnboardingContext with all flow variables
 */
export function buildOnboardingContext(state: RootState): OnboardingContext {
  const { consultation, questionnaire, searchParams } = state;
  const mode = state.graph.mode;
  const patientDataCollectionConfig = resolvePatientDataCollectionConfig(
    consultation.practice?.patientDataCollectionConfig,
  );

  return {
    patientOnboarding: questionnaire.config?.patientOnboarding ?? 'DEFAULT',
    patientOffboarding: isWebsiteDemoPractice(consultation.practice?.slug)
      ? 'CONFIRMATION_ONLY'
      : questionnaire.config?.patientOffboarding ?? 'DEFAULT',
    mode: mode ?? 'PRACTICE_LINK',
    patientDateOfBirth: consultation.patient?.dateOfBirth
      ? String(consultation.patient.dateOfBirth)
      : null,
    isPatientInformationComplete: consultation.patient
      ? isPatientInformationComplete(consultation.patient, {
          requireDateOfBirthAndSex: !isKioskOnboarding(
            questionnaire.config?.patientOnboarding,
          ),
          patientDataCollectionConfig,
        })
      : false,
    isInvitation: mode === 'INVITATION',
    isPatientAuthenticated: !!consultation.patient?.id,
    noAppointment: consultation.noAppointment ?? false,
    selfParam: searchParams?.['self'],
    gotoParam: searchParams?.['goto'],
    skipProfessionalSelection:
      consultation.practice?.skipProfessionalSelection ?? false,
    assistedByPro: state.graph.options?.ASSISTED_BY_PRO ?? false,
    hasAssignedProfessional: !!consultation.professional?.id,
    // Derived from consultation.date, NOT graph.options.CONSULTATION_TIME_KNOWN:
    // setQuestionnaireConfig rebuilds graph.options from defaults for non-default
    // variants, which clears that flag mid-flow. It does not touch
    // state.consultation, so consultation.date is the durable known-date signal.
    consultationTimeKnown: !!consultation.date,
    patientDocumentsEnabled:
      consultation.professional?.patientDocumentsEnabled ?? false,
    allowAdditionalUploads:
      consultation.professional?.patientDocumentAdditionalUploadsEnabled ??
      false,
    patientDataCollectionConfig,
    experimentFlags: state.graph.experimentFlags ?? {},
  };
}
